import React, { useMemo, useRef, useState } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import { getAlquilerByDate } from "../../../api/Alquileres";
import ModalAlquiler from "./ModalAlquiler";

function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={placeholder || "Filtrar..."}
      style={{ minWidth: 100 }}
    />
  );
}

export default function AlquileresByDate() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const tableRef = useRef(null);

  // 🔹 Buscar alquileres por fecha
  const handleBuscar = async () => {
    if (!fechaInicio || !fechaFin) {
      return Swal.fire("Faltan fechas", "Debe seleccionar un rango de fechas", "warning");
    }

    if (fechaInicio > fechaFin) {
      return Swal.fire("Rango inválido", "La fecha inicio no puede ser mayor que la fin", "warning");
    }

    try {
      setLoading(true);
      const res = await getAlquilerByDate({ fechainicio: fechaInicio, fechafin: fechaFin });
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      Swal.fire("Error", err.message || "Error al cargar alquileres", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Columnas
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "idalquiler", className: "text-center", minWidth: 60 },
      { Header: "Código", accessor: "coditem", className: "text-center", minWidth: 80 },
      { Header: "Ítem", accessor: "item", minWidth: 200 },
      { Header: "Usuario", accessor: "usuario", minWidth: 160 },
      { Header: "Fecha Inicio", accessor: "fechainicio", className: "text-center", minWidth: 120 },
      { Header: "Fecha Fin", accessor: "fechafin", className: "text-center", minWidth: 120 },
      {
        Header: "Monto (Gs)",
        accessor: "monto",
        className: "text-end",
        minWidth: 120,
        Cell: ({ value }) => (value ? value.toLocaleString("es-PY") : "-"),
      },
      { Header: "Factura", accessor: "factura", className: "text-center", minWidth: 120 },
      {
        Header: "Estado",
        accessor: "estado",
        className: "text-center",
        minWidth: 100,
        Cell: ({ value }) => (
          <span
            className={`badge ${
              value?.toUpperCase() === "ACTIVO" ? "bg-success" : "bg-danger"
            }`}
            style={{ fontSize: 12 }}
          >
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Opciones",
        accessor: "acciones",
        disableFilters: true,
        disableGlobalFilter: true,
        className: "text-center",
        width: 100,
        Cell: ({ row }) => (
          <button
            className="btn btn-outline-warning btn-sm"
            title="Ver detalles"
            onClick={() => {
              setSelectedItem(row.original);
              setShowModal(true);
            }}
          >
            <i className="fas fa-eye"></i>
          </button>
        ),
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  // 🔹 Configuración de la tabla
  const tableInstance = useTable(
    { columns, data, defaultColumn, initialState: { pageSize: 15 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    rows,
    gotoPage,
    pageCount,
    pageOptions,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // 🔹 Exportar Excel
  const handleExportExcel = () => {
    if (!rows || rows.length === 0)
      return Swal.fire("Sin datos", "No hay datos para exportar", "info");

    const mapped = rows.map((r) => ({
      ID: r.original.idalquiler,
      Código: r.original.coditem,
      Ítem: r.original.item,
      Usuario: r.original.usuario,
      "Fecha Inicio": r.original.fechainicio,
      "Fecha Fin": r.original.fechafin,
      Monto: r.original.monto,
      Factura: r.original.factura,
      Estado: r.original.estado,
    }));

    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AlquileresPorFecha");
    XLSX.writeFile(wb, `Alquileres_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  return (
    <div className="mt-3">
      {/* 🔹 Filtros */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label mb-1">Fecha inicio</label>
          <input
            type="date"
            className="form-control"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label mb-1">Fecha fin</label>
          <input
            type="date"
            className="form-control"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>
        <div className="col-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleBuscar}
            disabled={!fechaInicio || !fechaFin || loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={handleExportExcel}
            disabled={loading || !rows || rows.length === 0}
          >
            Exportar Excel
          </button>
        </div>
        <div className="col-auto ms-auto">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar en todo…"
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
      </div>

      {/* 🔹 Tabla */}
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "30vh" }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              border: "4px solid #ddd",
              borderTop: "4px solid #1569c9ff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>
            Cargando alquileres...
          </span>
        </div>
      ) : (
        <div className="table-responsive">
          <table {...getTableProps()} className="table table-striped table-bordered table-sm">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <React.Fragment key={i}>
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th key={column.id}>{column.render("Header")}</th>
                    ))}
                  </tr>
                  <tr>
                    {headerGroup.headers.map((column) => (
                      <th key={`${column.id}-filter`}>
                        {column.canFilter ? column.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔹 Paginación */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <span>
          Página <strong>{pageIndex + 1}</strong> de {pageOptions.length || 1}
        </span>
        <div>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            {"<<"}
          </button>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Anterior
          </button>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Siguiente
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
        </div>
      </div>

      {/* 🔹 Modal solo visualización */}
      {showModal && (
        <ModalAlquiler
          show={showModal}
          onHide={() => setShowModal(false)}
          alquilerData={selectedItem}
          viewOnly={true} // 👁️ modo solo lectura
        />
      )}
    </div>
  );
}
